import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faAnglesLeft,
  faAnglesRight,
} from '@fortawesome/free-solid-svg-icons';

const btnBase =
  'inline-flex items-center justify-center w-8 h-8 rounded-md text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/40';
const btnActive = `${btnBase} bg-primaryColor text-white`;
const btnInactive = `${btnBase} text-slate-600 hover:bg-slate-100`;
const btnDisabled = `${btnBase} text-slate-300 cursor-not-allowed`;

const Pagination = ({
  page = 1,
  between = 3,
  total,
  limit,
  changePage = () => {},
  next = true,
  last = false,
  ellipsis = 0,
}) => {
  const { t } = useTranslation('common');
  const totalPages = Math.ceil(total / limit);
  between = Math.max(between, 1);
  page = Math.max(Math.min(page, totalPages), 1);
  ellipsis = ellipsis < 1 ? 0 : Math.min(ellipsis + 2, between);

  const positions = Array.from({ length: totalPages }, (_, i) => i);

  const qtdPages = between * 2 + 1;
  const range =
    totalPages <= qtdPages
      ? positions
      : page - 1 <= between
        ? positions.slice(0, qtdPages - (ellipsis ? ellipsis + 1 : 0))
        : page + between >= totalPages
          ? positions.slice(totalPages - qtdPages + (ellipsis ? ellipsis + 1 : 0), totalPages)
          : positions.slice(
              page - 1 - (between - (ellipsis ? ellipsis + 1 : 0)),
              page + (between - (ellipsis ? ellipsis + 1 : 0)),
            );

  return total > 0 ? (
    <div className="flex items-center gap-0.5">
      {last && (
        <button
          className={page <= 1 ? btnDisabled : btnInactive}
          onClick={() => page > 1 && changePage(1)}
          disabled={page <= 1}
          aria-label={t('common:pagination.firstPageAria', 'First page')}
        >
          <FontAwesomeIcon icon={faAnglesLeft} />
        </button>
      )}

      {next && (
        <button
          className={page <= 1 ? btnDisabled : btnInactive}
          onClick={() => page > 1 && changePage(page - 1)}
          disabled={page <= 1}
          aria-label={t('common:pagination.previousPageAria', 'Previous page')}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
      )}

      {totalPages > between * 2 + 1 &&
        ellipsis > 0 &&
        positions.slice(0, page - 1 <= between ? 0 : ellipsis).map((value) => (
          <button
            key={value}
            className={btnInactive}
            onClick={() => changePage(value + 1)}
          >
            {value + 1}
          </button>
        ))}

      {totalPages > between * 2 + 1 && ellipsis > 0 && page - 1 > between && (
        <span className="w-8 h-8 inline-flex items-center justify-center text-slate-400 text-sm select-none">
          …
        </span>
      )}

      {range.map((value) => (
        <button
          key={value}
          className={value === page - 1 ? btnActive : btnInactive}
          onClick={() => changePage(value + 1)}
        >
          {value + 1}
        </button>
      ))}

      {totalPages > between * 2 + 1 && ellipsis > 0 && page < totalPages - between && (
        <span className="w-8 h-8 inline-flex items-center justify-center text-slate-400 text-sm select-none">
          …
        </span>
      )}

      {totalPages > between * 2 + 1 &&
        ellipsis > 0 &&
        positions
          .slice(page >= totalPages - between ? totalPages : totalPages - ellipsis, totalPages)
          .map((value) => (
            <button
              key={value}
              className={btnInactive}
              onClick={() => changePage(value + 1)}
            >
              {value + 1}
            </button>
          ))}

      {next && (
        <button
          className={page >= totalPages ? btnDisabled : btnInactive}
          onClick={() => page < totalPages && changePage(page + 1)}
          disabled={page >= totalPages}
          aria-label={t('common:pagination.nextPageAria', 'Next page')}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      )}

      {last && (
        <button
          className={page >= totalPages ? btnDisabled : btnInactive}
          onClick={() => page < totalPages && changePage(totalPages)}
          disabled={page >= totalPages}
          aria-label={t('common:pagination.lastPageAria', 'Last page')}
        >
          <FontAwesomeIcon icon={faAnglesRight} />
        </button>
      )}
    </div>
  ) : null;
};

export default Pagination;
